import { Channel, EspBinding } from 'api/ChannelsAPI';
import { MessageType } from 'api/MessageTypeAPI';
import { UserContext } from 'core/userContext';
import { SUBSCRIPTION_POLICY } from 'modules/channels/constants';
import {
  createFrequencyCapMetadata,
  createRateLimitMetadata,
  getEspAccountById,
} from 'modules/channels/utils';
import { EspAccount, SERVICE_PROVIDERS, SubscriptionPolicy } from 'modules/graphql/types';
import styled from 'styled-components';
import { SearchableText } from 'ui-beta/searchableText';
import { ClickToCopyCode } from 'ui/clickToCopy';
import { colors, spacing } from 'ui/theme';
import { RightTooltip, Tooltip } from 'ui/tooltip';

const MetaRow = styled.span`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  margin-top: ${spacing.half};
`;

const MetaItem = styled.span`
  display: inline-flex;
  gap: ${spacing.quarter};

  & + & {
    &:before {
      content: '•';
      padding-right: ${spacing.half};
      line-height: 1.2;
    }
  }
`;

const Ul = styled.ul`
  padding-left: ${spacing.base};
  margin-bottom: 0;

  > li {
    list-style-type: disc;
    word-break: break-word;

    span {
      display: inline-block;

      + span {
        margin-left: ${spacing.quarter};
      }
    }
  }
`;

const StyledClickToCopyCode = styled(ClickToCopyCode)`
  padding: ${spacing.eighth} ${spacing.quarter};

  code {
    color: ${colors.textPrimary} !important;
  }
`;

export const TEXT = {
  itemName: {
    singular: 'sending platform',
    plural: 'sending platforms',
  },
  tooltipContent: {
    [SubscriptionPolicy.OptIn]:
      'Users must actively subscribe to this message type to receive messages.',
    [SubscriptionPolicy.OptOut]:
      'Users will automatically receive messages from this message type.',
    [SubscriptionPolicy.DoubleOptIn]:
      'Users will have to confirm their subscription after inputting their phone number.',
  },
};

const COPY_ID_TOOLTIP_CONTENT = {
  default: 'Click to copy message type ID',
  success: 'Copied!',
  error: 'Failed to copy message type ID',
};

type MetdataTooltipContent = {
  espAccounts: EspAccount[];
  espBindings: EspBinding[];
};

const MetadataTooltipContent = ({ espAccounts, espBindings = [] }: MetdataTooltipContent) => {
  return (
    <>
      {espBindings.length > 0 && (
        <Ul>
          {espBindings?.map(({ espAccountId }) => {
            const currentAccount = getEspAccountById(espAccounts, espAccountId);
            const isSparkpostAccount =
              currentAccount?.esp === SERVICE_PROVIDERS.SPARKPOST ||
              currentAccount?.esp === SERVICE_PROVIDERS.SPARKPOST_ELITE;
            return (
              <li key={espAccountId}>
                <span>{currentAccount?.name}</span>
                <span>(ID {espAccountId})</span>
                {isSparkpostAccount && (
                  <span>{currentAccount?.binding ? currentAccount.binding : 'default pool'}</span>
                )}
              </li>
            );
          })}
        </Ul>
      )}
    </>
  );
};

export type MessageTypeMetadataProps = {
  channel: Channel;
  inputValue: string;
  messageType: MessageType;
  featureFlags: UserContext['featureFlags'];
};

export const MessageTypeMetadata = ({
  channel,
  inputValue,
  messageType,
  featureFlags,
}: MessageTypeMetadataProps) => {
  const { id, subscriptionPolicy, rateLimitPerMin, frequencyCap } = messageType;
  const { CampaignRateLimiting, MessageTypeSubscription, MessageTypeRateLimiting } = featureFlags;
  const frequencyCapMetadata = createFrequencyCapMetadata(frequencyCap, channel, featureFlags);
  const rateLimitMetadata = createRateLimitMetadata(rateLimitPerMin);
  const tooltipContent = TEXT.tooltipContent[subscriptionPolicy];
  const isRateLimitEnabled = !!CampaignRateLimiting && !!MessageTypeRateLimiting;

  return (
    <MetaRow>
      <MetaItem>
        <StyledClickToCopyCode
          content={`${id}`}
          isCopyable
          prependContent="ID"
          tooltipContent={COPY_ID_TOOLTIP_CONTENT}
        >
          <SearchableText searchText={inputValue} text={`${id}`} />
        </StyledClickToCopyCode>
      </MetaItem>
      {MessageTypeSubscription && (
        <MetaItem>
          <Tooltip isUnderlined tooltipContent={tooltipContent}>
            {SUBSCRIPTION_POLICY[subscriptionPolicy]}
          </Tooltip>
        </MetaItem>
      )}
      {isRateLimitEnabled && <MetaItem>{rateLimitMetadata}</MetaItem>}
      {/* makes all feature flag checks in metadata function and if anything is returned show the meta item */}
      {frequencyCapMetadata && <MetaItem>{frequencyCapMetadata}</MetaItem>}
    </MetaRow>
  );
};

export type ChannelMetadataProps = {
  channel: Channel;
  espAccounts: EspAccount[];
  inputValue: string;
};

export const ChannelMetadata = ({ channel, espAccounts, inputValue }: ChannelMetadataProps) => {
  const { id, channelType, espBindings = [] } = channel;
  const bindingsLength = espBindings.length;
  const formattedItemName = bindingsLength === 1 ? TEXT.itemName.singular : TEXT.itemName.plural;

  return (
    <MetaRow>
      <MetaItem>
        <span>ID</span>
        <span>
          <SearchableText searchText={inputValue} text={`${id}`} />
        </span>
      </MetaItem>
      <MetaItem>{channelType}</MetaItem>
      {bindingsLength > 0 && (
        <MetaItem>
          <RightTooltip
            isUnderlined
            tooltipContent={
              <MetadataTooltipContent espAccounts={espAccounts} espBindings={espBindings} />
            }
          >
            <span>{`${bindingsLength} ${formattedItemName}`}</span>
          </RightTooltip>
        </MetaItem>
      )}
    </MetaRow>
  );
};
